import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PERKS = [
  "Full access for 30 days — no card required",
  "Vehicle MOT, service & tax tracking",
  "Driver licence, CPC & tachograph reminders",
  "Defect reports, documents & audit-ready PDFs",
];

export default function TrialSignup() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({ company_name: "", name: "", email: "", password: "", region: "UK" });
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!agreed) { toast.error("Please agree to the Privacy Policy to continue"); return; }
    setBusy(true);
    try {
      const res = await api.post("/auth/trial-signup", {
        company_name: form.company_name.trim(), name: form.name.trim(),
        email: form.email.trim(), password: form.password, region: form.region,
      });
      loginWithToken(res.data.token, res.data.user);
        toast.success("Your 30-day trial has started — welcome to HaulCheck!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start your trial");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="trial-signup-page">
      <div className="relative hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-25 bg-cover bg-center"
          style={{ backgroundImage: "url('https://customer-assets-v7afamib.emergentagent.net/job_transport-verify-3/artifacts/y6fks66u_Workshop.jpg')" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-slate-900/40" />
        <div className="relative flex items-center gap-2">
          <Truck size={28} />
          <span className="font-heading font-black text-xl tracking-tight">HAULCHECK</span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]">
            Start your free<br />30-day trial.
          </h1>
          <p className="mt-5 text-slate-300 max-w-md text-base">
            Set up your own private HaulCheck compliance account in under a minute. No invitation, no credit card.
          </p>
          <ul className="mt-8 space-y-2.5">
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-2.5 text-sm text-slate-300">
                <CheckCircle2 size={17} className="text-emerald-400 shrink-0" /> {p}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-slate-500 tracking-widest uppercase">Fleet Compliance Control Room</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-white">
        <div className="w-full max-w-md animate-in-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Truck size={26} className="text-slate-900" />
            <span className="font-heading font-black text-lg tracking-tight">HAULCHECK</span>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Create your account</h2>
          <p className="text-slate-500 mt-2 text-sm">Your 30-day trial starts instantly. You'll be signed straight in.</p>

          <form onSubmit={submit} className="space-y-4 mt-6">
            <div>
              <Label htmlFor="ts-company">Company name</Label>
              <Input data-testid="trial-company-input" id="ts-company" required value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Acme Haulage Ltd" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ts-name">Your name</Label>
              <Input data-testid="trial-name-input" id="ts-name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex Morgan" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ts-email">Work email</Label>
              <Input data-testid="trial-email-input" id="ts-email" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.co.uk" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ts-password">Password</Label>
              <Input data-testid="trial-password-input" id="ts-password" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="ts-region">Region</Label>
              <select data-testid="trial-region-select" id="ts-region" value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                className="mt-1.5 w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white">
                <option value="UK">United Kingdom (DVSA)</option>
                <option value="IE">Ireland (RSA)</option>
                <option value="EU">Europe (EU)</option>
              </select>
            </div>
            <label className="flex items-start gap-2.5 cursor-pointer select-none" data-testid="trial-agree-label">
              <input type="checkbox" data-testid="trial-agree-checkbox" checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 shrink-0" />
              <span className="text-xs text-slate-500 leading-relaxed">
                I agree to the <Link to="/privacy" target="_blank" className="text-slate-800 font-semibold underline">Privacy Policy</Link> and{" "}
                <Link to="/dpa" target="_blank" className="text-slate-800 font-semibold underline">Data Processing Agreement</Link>.
              </span>
            </label>
            <Button data-testid="trial-signup-button" type="submit" disabled={busy || !agreed}
              className="w-full bg-black hover:bg-slate-800 text-white py-2.5 rounded-md font-semibold disabled:opacity-50">
              {busy ? "Starting your trial…" : "Start my 30-day free trial"}
            </Button>
            <p className="text-center text-xs text-slate-400">
              Already have an account? <button type="button" onClick={() => navigate("/login")} className="text-slate-700 font-semibold underline">Sign in</button>
            </p>
            <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400"><ShieldCheck size={13} /> No credit card required · Cancel anytime, no contract</p>
          </form>
        </div>
      </div>
    </div>
  );
}
